import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { StickyNote, Briefcase, Send, Save, Search, X, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Home-page quick-note dialog (Feb 2026).
 *
 * Unlike QuickJobNoteDialog (which is always scoped to one job), this one is
 * opened from the mobile home screen with NO job context. The worker can:
 *   • Type a free note (auto-stamped author + timestamp)
 *   • OPTIONALLY attach it to one of their assigned jobs (job picker)
 *   • OPTIONALLY send it to a teammate (user picker)
 *
 * Save routing
 * ------------
 *  - If a job is picked → POST /api/jobs/{job_id}/notes with
 *    notify_to=[recipient_id] (when also picked). This lights up the job's
 *    note thread + fires the in-app bell + best-effort email.
 *  - If NO job → POST /api/notes (personal notes inbox). When a recipient
 *    is also chosen we then POST /api/notes/send to drop a copy into the
 *    teammate's personal inbox (separate id so they can edit/delete).
 *
 * The dialog is intentionally lightweight — small textarea, big touch
 * targets, lined paper aesthetic. Designed for mobile-first single-thumb use.
 */
export default function QuickHomeNoteDialog({ open, onClose, token, currentUser }) {
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [showJobPicker, setShowJobPicker] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [jobSearch, setJobSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [pickedJob, setPickedJob] = useState(null);
  const [pickedUser, setPickedUser] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reload pickers each time the dialog opens so the lists are fresh.
  useEffect(() => {
    if (!open || !token) return;
    setText('');
    setTitle('');
    setShowJobPicker(false);
    setShowUserPicker(false);
    setJobSearch('');
    setUserSearch('');
    setPickedJob(null);
    setPickedUser(null);

    // Pull just the user's assigned jobs (not the whole company) — that's
    // what mobile users would attach a note to.
    axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.jobs || []);
        // Surface active jobs first — workers most often attach to in-progress / todo.
        const order = { in_progress: 0, paused: 1, todo: 2, pending: 2, completed: 3 };
        const ranked = list
          .filter((j) => !j.deleted_at && !j.archived)
          .sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));
        setJobs(ranked);
      })
      .catch(() => setJobs([]));

    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setUsers((r.data || []).filter((u) => u.id !== currentUser?.id && !u.deleted_at)))
      .catch(() => setUsers([]));
  }, [open, token, currentUser?.id]);

  const now = useMemo(() => new Date(), [open]);

  const filteredJobs = useMemo(() => {
    const q = jobSearch.trim().toLowerCase();
    if (!q) return jobs.slice(0, 25);
    return jobs.filter((j) => [j.title, j.job_number, j.customer_name, j.city]
      .some((v) => (v || '').toLowerCase().includes(q))).slice(0, 25);
  }, [jobSearch, jobs]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users.slice(0, 20);
    return users.filter((u) => [u.name, u.email, u.role]
      .some((v) => (v || '').toLowerCase().includes(q))).slice(0, 20);
  }, [userSearch, users]);

  const canSave = text.trim().length >= 1 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      if (pickedJob) {
        // Path A/B — note is job-attached. Goes into the job's notes thread.
        await axios.post(
          `${API}/jobs/${pickedJob.id}/notes`,
          { text: text.trim(), notify_to: pickedUser ? [pickedUser.id] : [] },
          auth,
        );
        toast({
          title: pickedUser ? 'Note saved & sent' : 'Note saved to job',
          description: pickedUser
            ? `Attached to ${pickedJob.job_number || pickedJob.title} and sent to ${pickedUser.name}.`
            : `Attached to ${pickedJob.job_number || pickedJob.title}.`,
        });
      } else {
        // Path C/D — personal note. Save to own notes inbox first.
        const noteRes = await axios.post(
          `${API}/notes`,
          {
            title: title.trim() || null,
            body: text.trim(),
            job_id: null,
            tags: [],
          },
          auth,
        );
        if (pickedUser && noteRes?.data?.id) {
          // Forward a copy to the teammate (creates a copy in their inbox).
          await axios.post(
            `${API}/notes/send`,
            { note_id: noteRes.data.id, recipient_user_id: pickedUser.id },
            auth,
          );
        }
        toast({
          title: pickedUser ? 'Note saved & sent' : 'Note saved',
          description: pickedUser
            ? `Saved to your notes and sent to ${pickedUser.name}.`
            : 'Saved to your personal notes.',
        });
      }
      onClose();
    } catch (e) {
      toast({
        title: 'Could not save note',
        description: e.response?.data?.detail || e.message || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-[#1A2433] text-white max-w-md p-0 overflow-hidden border-amber-500/30"
        data-testid="home-note-dialog"
      >
        {/* Notepad header */}
        <DialogHeader className="px-5 py-4 bg-gradient-to-r from-amber-500/10 to-amber-700/5 border-b border-amber-500/30">
          <DialogTitle className="flex items-center gap-2 text-white">
            <StickyNote className="w-5 h-5 text-amber-400" />
            <span className="font-semibold">Quick Note</span>
          </DialogTitle>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
            <span>By <strong className="text-white">{currentUser?.name || 'You'}</strong></span>
            <span>{now.toLocaleString()}</span>
          </div>
        </DialogHeader>

        <div className="p-5 space-y-3">
          {/* Optional title */}
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            maxLength={120}
            className="bg-[#0F1923] border-[#2D3748] text-white"
            data-testid="home-note-title"
          />

          {/* Body — lined paper */}
          <div className="rounded-md border border-amber-500/30 bg-amber-50/[0.03] p-1">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What's on your mind? You can attach this to a job or send it to a teammate below."
              rows={6}
              maxLength={4000}
              autoFocus
              className="bg-transparent border-0 focus-visible:ring-0 text-white resize-none text-base"
              data-testid="home-note-text"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(transparent, transparent 27px, rgba(255,255,255,0.05) 28px)',
                lineHeight: '28px',
              }}
            />
            <div className="px-2 pb-1 text-right text-xs text-slate-500">{text.length}/4000</div>
          </div>

          {/* Attach to job (collapsible) */}
          <div className="space-y-2">
            {pickedJob ? (
              <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/40 rounded px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Briefcase className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{pickedJob.title}</p>
                    {pickedJob.job_number && (
                      <p className="text-[11px] font-mono text-emerald-300">{pickedJob.job_number}</p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPickedJob(null)}
                  className="text-slate-400 hover:text-white p-1"
                  data-testid="home-note-job-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setShowJobPicker((s) => !s); setShowUserPicker(false); }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-sm transition ${
                  showJobPicker
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                    : 'border-[#2D3748] text-slate-300 hover:border-emerald-500/50'
                }`}
                data-testid="home-note-attach-job"
              >
                <span className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Attach to a job (optional)
                </span>
                <span className="text-xs text-slate-500">{showJobPicker ? 'Hide' : 'Pick'}</span>
              </button>
            )}

            {showJobPicker && !pickedJob && (
              <div className="border border-emerald-500/30 rounded-md p-2 bg-emerald-500/[0.04] space-y-2" data-testid="home-note-job-picker">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={jobSearch}
                    onChange={(e) => setJobSearch(e.target.value)}
                    placeholder="Search your jobs…"
                    className="pl-8 bg-[#0F1923] border-[#2D3748] text-white text-sm h-8"
                    data-testid="home-note-job-search"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1" data-testid="home-note-job-list">
                  {filteredJobs.length === 0 ? (
                    <p className="text-xs text-slate-500 italic px-1 py-1">
                      {jobs.length === 0 ? 'No jobs assigned yet.' : 'No matches.'}
                    </p>
                  ) : filteredJobs.map((j) => (
                    <button
                      type="button"
                      key={j.id}
                      onClick={() => { setPickedJob(j); setShowJobPicker(false); }}
                      className="w-full flex items-start gap-2 px-2 py-1.5 rounded hover:bg-emerald-500/10 text-left text-sm"
                      data-testid={`home-note-job-${j.id}`}
                    >
                      {j.job_number && (
                        <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded mt-0.5 flex-shrink-0">
                          {j.job_number}
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{j.title}</p>
                        <p className="text-[11px] text-slate-400 truncate">
                          {j.customer_name} {j.status ? `· ${String(j.status).replace(/_/g, ' ')}` : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Send to teammate (collapsible) */}
          <div className="space-y-2">
            {pickedUser ? (
              <div className="flex items-center justify-between bg-blue-500/10 border border-blue-500/40 rounded px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Send className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{pickedUser.name}</p>
                    <p className="text-[11px] text-slate-400 truncate">{pickedUser.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPickedUser(null)}
                  className="text-slate-400 hover:text-white p-1"
                  data-testid="home-note-user-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => { setShowUserPicker((s) => !s); setShowJobPicker(false); }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-sm transition ${
                  showUserPicker
                    ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                    : 'border-[#2D3748] text-slate-300 hover:border-blue-500/50'
                }`}
                data-testid="home-note-send-user"
              >
                <span className="flex items-center gap-2">
                  <Send className="w-4 h-4" /> Send to a teammate (optional)
                </span>
                <span className="text-xs text-slate-500">{showUserPicker ? 'Hide' : 'Pick'}</span>
              </button>
            )}

            {showUserPicker && !pickedUser && (
              <div className="border border-blue-500/30 rounded-md p-2 bg-blue-500/[0.04] space-y-2" data-testid="home-note-user-picker">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    placeholder="Search teammates by name, email, role…"
                    className="pl-8 bg-[#0F1923] border-[#2D3748] text-white text-sm h-8"
                    data-testid="home-note-user-search"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1" data-testid="home-note-user-list">
                  {filteredUsers.length === 0 ? (
                    <p className="text-xs text-slate-500 italic px-1 py-1">No teammates match.</p>
                  ) : filteredUsers.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => { setPickedUser(u); setShowUserPicker(false); }}
                      className="w-full px-2 py-1.5 rounded hover:bg-blue-500/10 text-left text-sm"
                      data-testid={`home-note-user-${u.id}`}
                    >
                      <p className="font-medium text-white truncate">{u.name}</p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {u.email} · {u.role}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t border-[#2D3748]">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="home-note-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!canSave}
              className={pickedUser ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}
              data-testid="home-note-save"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : pickedUser ? (
                <><Send className="w-4 h-4 mr-2" /> {pickedJob ? 'Save & Send' : 'Save & Send'}</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Note</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
